import Vue from 'vue'
import Vuex, { ActionTree, ActionContext } from 'vuex'
import {
  adminSignIn,
  forgotPassword,
  resetPassword,
  createUser,
  updateUser,
  createJob,
  updateJob,
  updateBranch,
  createBranch,
  updateEmployer,
  createRuleEngineAlgorithm,
  updateRuleEngineAlgorithm,
  createJobListing,
  updateJobListing,
  updateArticle,
  createArticle,
  updateConfiguration,
  createArea,
  updateArea,
  createAuthority,
  updateAuthority,
  upsertJobListingPlanConfiguration,
  disallowEmailNotifications
} from '@/utils/mutations'
import {
  getUsers,
  getUserData,
  getJobs,
  getBranches,
  getRuleEngineBlocks,
  getRuleEngineAlgorithms,
  getJobListings,
  getArticles,
  getConfigurations,
  getAreas,
  getAuthorities,
  getAreasBasic,
  getAuthoritiesBasic,
  getJobListingConfiguration
} from '@/utils/queries'
import {
  State,
  User,
  FetchUsersPayload,
  Job,
  FetchBranchesPayload,
  Branch,
  FetchJobsPayload,
  ArchivedPayload,
  RuleEngineFact,
  RuleEngineOperator,
  RuleEngineAlgorithm,
  FetchJobListingsPayload,
  JobListing,
  FetchUserDataPayload,
  UpdateJobPayload,
  CreateJobPayload,
  Article,
  FetchArticlesPayload,
  UpdateArticlePayload,
  Configuration,
  UpdateConfigurationPayload,
  FetchAreasPayload,
  Area,
  FetchAuthoritiesPayload,
  Authority,
  AuthorityCreateInput,
  UpdateAuthorityPayload,
  UpdateJobListingPayload,
  JobListingPlanConfiguration
} from '@/utils/types'
import { makeGqlRequest, recursiveReduceCondition, translateAgeGroupsToDates } from '@/utils/helpers'
import createPersistedState from 'vuex-persistedstate'

Vue.use(Vuex)

const state: State = {
  areas: [],
  authorities: [],
  branches: [],
  configurations: [],
  user: {},
  users: [],
  jobs: [],
  jobListings: [],
  jobListingPlanConfigurations: [],
  articles: [],
  jwt: '',
  ruleEngineAlgorithms: [],
  ruleEngineFacts: [],
  ruleEngineOperators: [],
  totalAreas: 0,
  totalAuthorities: 0,
  totalUsers: 0,
  totalBranches: 0,
  totalJobs: 0,
  totalJobListings: 0,
  totalArticles: 0
}

const getters = {
  areas: (state: State) => state.areas,
  authorities: (state: State) => state.authorities,
  branches: (state: State) => state.branches,
  configurations: (state: State) => state.configurations,
  jwt: (state: State) => state.jwt,
  users: (state: State) => state.users,
  jobs: (state: State) => state.jobs,
  jobListings: (state: State) => state.jobListings,
  jobListingPlanConfigurations: (state: State) => state.jobListingPlanConfigurations,
  articles: (state: State) => state.articles,
  ruleEngineAlgorithms: (state: State) => state.ruleEngineAlgorithms,
  ruleEngineFacts: (state: State) => state.ruleEngineFacts,
  ruleEngineOperators: (state: State) => state.ruleEngineOperators,
  totalAreas: (state: State) => state.totalAreas,
  totalAuthorities: (state: State) => state.totalAuthorities,
  totalBranches: (state: State) => state.totalBranches,
  totalJobListings: (state: State) => state.totalJobListings,
  totalJobs: (state: State) => state.totalJobs,
  totalUsers: (state: State) => state.totalUsers
}

const mutations = {
  addArea: (state: State, area: Area) => state.areas.push(area),
  addAuthority: (state: State, authority: Authority) => state.authorities.push(authority),
  addArticle (state: State, article: Article) {
    state.articles.push(article)
  },
  addBranch (state: State, branch: Branch) {
    state.branches.push(branch)
  },
  addJob (state: State, job: Job) {
    state.jobs.push(job)
  },
  addJobListing (state: State, jobListing: JobListing) {
    state.jobListings.push(jobListing)
  },
  addUser (state: State, user: User) {
    state.users.push(user)
  },
  archiveArea (state: State, area: Area) {
    const AreaIndexInState = state.areas.findIndex(u => u.id === area.id)
    state.areas.splice(AreaIndexInState, 1)
  },
  setAreas (state: State, areas: Area[]) {
    state.areas = areas
  },
  setAuthorities (state: State, authorities: Authority[]) {
    state.authorities = authorities
  },
  setBranches (state: State, branches: Branch[]) {
    state.branches = branches
  },
  setConfigurations (state: State, configurations: Configuration[]) {
    state.configurations = configurations
  },
  setRuleEngineAlgorithms (state: State, algorithms: RuleEngineAlgorithm[]) {
    state.ruleEngineAlgorithms = algorithms
  },
  setRuleEngineFacts (state: State, facts: RuleEngineFact[]) {
    state.ruleEngineFacts = facts
  },
  setRuleEngineOperators (state: State, operators: RuleEngineOperator[]) {
    state.ruleEngineOperators = operators
  },
  setTotalAreas (state: State, value: number) {
    state.totalAreas = value
  },
  setTotalAuthorities (state: State, value: number) {
    state.totalAuthorities = value
  },
  setTotalBranches (state: State, value: number) {
    state.totalBranches = value
  },
  setToken (state: State, jwt: string) {
    state.jwt = jwt
  },
  setUser (state: State, user: User) {
    state.user = user
  },
  setUsers (state: State, users: []) {
    state.users = users
  },
  setJobs (state: State, jobs: Job[]) {
    state.jobs = jobs
  },
  setArticles (state: State, articles: Article[]) {
    state.articles = articles
  },
  setTotalUsers (state: State, count: number) {
    state.totalUsers = count
  },
  updateUser (state: State, user: User) {
    const userIndexInState = state.users.findIndex(u => u.id === user.id)
    Vue.set(state.users, userIndexInState, user)
  },
  updateBranch (state: State, branch: Branch) {
    const branchIndexInState = state.branches.findIndex(b => b.id === branch.id)
    Vue.set(state.branches, branchIndexInState, branch)
  },
  archiveUser (state: State, user: User) {
    const userIndexInState = state.users.findIndex(u => u.id === user.id)
    state.users.splice(userIndexInState, 1)
  },
  deleteJob (state: State, job: Job) {
    const jobIndexInState = state.jobs.findIndex(j => j.id === job.id)
    state.jobs.splice(jobIndexInState, 1)
  },
  archiveBranch (state: State, branch: Branch) {
    const branchIndexInState = state.branches.findIndex(b => b.id === branch.id)
    state.branches.splice(branchIndexInState, 1)
  },
  updateJob (state: State, job: Job) {
    const jobIndexInState = state.jobs.findIndex(j => j.id === job.id)
    Vue.set(state.jobs, jobIndexInState, job)
  },
  updateArea (state: State, area: Area) {
    const areaIndexInState = state.areas.findIndex(a => a.id === area.id)
    Vue.set(state.areas, areaIndexInState, area)
  },
  updateArticle (state: State, article: Article) {
    const articleIndexInState = state.articles.findIndex(a => a.id === article.id)
    Vue.set(state.articles, articleIndexInState, article)
  },
  updateAuthority (state: State, authority: Authority) {
    const authorityIndexInState = state.authorities.findIndex(a => a.id === authority.id)
    Vue.set(state.authorities, authorityIndexInState, authority)
  },
  archiveAuthority (state: State, authority: Authority) {
    const authorityIndexInState = state.authorities.findIndex(a => a.id === authority.id)
    state.authorities.splice(authorityIndexInState, 1)
  },
  activateAuthority (state: State, authority: Authority) {
    const authorityIndexInState = state.authorities.findIndex(a => a.id === authority.id)
    state.authorities.splice(authorityIndexInState, 1)
  },
  updateConfiguration (state: State, configuration: Configuration) {
    const configIndexInState = state.configurations.findIndex(c => c.id === configuration.id)
    Vue.set(state.configurations, configIndexInState, configuration)
  },
  setTotalJobs (state: State, count: number) {
    state.totalJobs = count
  },
  setBranchToJob (state: State, { branch, job }: { branch: Branch; job: Job }) {
    const jobIndexInState = state.jobs.findIndex(j => j.id === job.id)
    Vue.set(state.jobs, jobIndexInState, { ...job, branch })
  },
  setJobListings (state: State, jobListings: JobListing[]) {
    state.jobListings = jobListings
  },
  setTotalJobListings (state: State, count: number) {
    state.totalJobListings = count
  },
  setJobListingConfigurations (state: State, jobListingConfigurations: JobListingPlanConfiguration[]) {
    state.jobListingPlanConfigurations = jobListingConfigurations
  },
  setTotalArticles (state: State, count: number) {
    state.totalArticles = count
  },
  updateJobListing (state: State, jobListing: JobListing) {
    const jobListingIndexInState = state.jobListings.findIndex(b => b.id === jobListing.id)
    Vue.set(state.jobListings, jobListingIndexInState, jobListing)
  }
}

const actions: ActionTree<State, State> = {
  async archiveArea (context: ActionContext<State, State>, payload: ArchivedPayload) {
    try {
      const res = await makeGqlRequest(updateArea, { id: payload.id, isArchived: payload.isArchived }, context.state.jwt)
      context.commit(mutations.archiveArea.name, res.updateArea)
      return true
    } catch (error) {
      return false
    }
  },
  async fetchAreas (context: ActionContext<State, State>, payload: FetchAreasPayload) {
    const res = await makeGqlRequest(payload.basic ? getAreasBasic : getAreas, payload, context.state.jwt)
    context.commit(mutations.setAreas.name, res.areas.areas)
    context.commit(mutations.setTotalAreas.name, res.areas.count)
  },
  async fetchAuthorities (context: ActionContext<State, State>, payload: FetchAuthoritiesPayload) {
    const res = await makeGqlRequest(payload.basic ? getAuthoritiesBasic : getAuthorities, payload, context.state.jwt)
    context.commit(mutations.setAuthorities.name, res.authorities.authorities)
    context.commit(mutations.setTotalAuthorities.name, res.authorities.count)
  },
  async fetchRuleEngineAlgorithms (context: ActionContext<State, State>, isActive: boolean) {
    const { ruleEngineAlgorithms }: {ruleEngineAlgorithms: RuleEngineAlgorithm[]} = await makeGqlRequest(getRuleEngineAlgorithms, { isActive }, context.getters.jwt)
    for (const algorithm of ruleEngineAlgorithms) {
      if (!algorithm.rules) {
        continue
      }
      for (const rule of algorithm.rules) {
        recursiveReduceCondition(rule.topCondition)
      }
    }
    context.commit(mutations.setRuleEngineAlgorithms.name, ruleEngineAlgorithms)
  },
  async fetchRuleEngineBlocks (context: ActionContext<State, State>) {
    const { ruleEngineBlocks } = await makeGqlRequest(getRuleEngineBlocks, undefined, context.getters.jwt)
    context.commit(mutations.setRuleEngineFacts.name, ruleEngineBlocks.facts)
    context.commit(mutations.setRuleEngineOperators.name, ruleEngineBlocks.operators)
  },
  async signIn (context: ActionContext<State, State>, user: { email: string; password: string }) {
    try {
      const res = await makeGqlRequest(adminSignIn, user)
      if (res.adminSignIn.token) {
        context.commit(mutations.setToken.name, res.adminSignIn.token)
        context.commit(mutations.setUser.name, res.adminSignIn.user)
        return true
      }
    } catch (error) {
      return false
    }
  },
  async sendForgotPassword (context: ActionContext<State, State>, email: string) {
    try {
      const success = await makeGqlRequest(forgotPassword, { email })
      return success
    } catch (error) {
      return false
    }
  },
  async sendResetPassword (context: ActionContext<State, State>, payload: { jwt: string; password: string }) {
    try {
      const success = await makeGqlRequest(resetPassword, { password: payload.password }, payload.jwt)
      return success
    } catch (error) {
      return false
    }
  },
  async sendDisallowEmailNotifications (context: ActionContext<State, State>, payload: { jwt: string }) {
    try {
      const success = await makeGqlRequest(disallowEmailNotifications, undefined, payload.jwt)
      return success
    } catch (error) {
      return false
    }
  },
  async fetchUsers (context: ActionContext<State, State>, payload: FetchUsersPayload) {
    const fixedPayload = {
      ...payload,
      birthdayLte: translateAgeGroupsToDates(payload.ages).lte,
      birthdayGt: translateAgeGroupsToDates(payload.ages).gt
    }
    delete fixedPayload.ages
    const res = await makeGqlRequest(getUsers, fixedPayload, context.state.jwt)
    context.commit(mutations.setUsers.name, res.users.users)
    context.commit(mutations.setTotalUsers.name, res.users.count)
  },
  async fetchUserData (context: ActionContext<State, State>, payload: FetchUserDataPayload): Promise<User> {
    const res = await makeGqlRequest(getUserData, payload, context.state.jwt)
    return res.user
  },
  async createUser (context: ActionContext<State, State>, payload: User) {
    try {
      const res = await makeGqlRequest(createUser, payload, context.state.jwt)
      context.commit(mutations.addUser.name, res.createUser)
      return true
    } catch (error) {
      return false
    }
  },
  async createJob (context: ActionContext<State, State>, payload: CreateJobPayload) {
    try {
      const createJobResult = await makeGqlRequest(createJob, payload, context.state.jwt)
      context.commit(mutations.addJob.name, createJobResult.createJob)
      return true
    } catch (error) {
      return false
    }
  },
  async createRuleEngineAlgorithm (context: ActionContext<State, State>, payload: RuleEngineAlgorithm) {
    try {
      await makeGqlRequest(createRuleEngineAlgorithm, { input: payload }, context.state.jwt)
      return true
    } catch (e) {
      return false
    }
  },
  async updateRuleEngineAlgorithm (context: ActionContext<State, State>, input: RuleEngineAlgorithm) {
    const id = input.id
    delete input.id
    try {
      await makeGqlRequest(
        updateRuleEngineAlgorithm, { id, input }, context.state.jwt)
      return true
    } catch (e) {
      return false
    }
  },
  async updateUser (context: ActionContext<State, State>, payload: User) {
    const mutation = payload.business ? updateEmployer : updateUser
    try {
      const res = await makeGqlRequest(mutation, payload, context.state.jwt)
      context.commit(mutations.updateUser.name, res.updateUser)
      return true
    } catch (error) {
      return false
    }
  },
  async fetchBranches (context: ActionContext<State, State>, payload: FetchBranchesPayload) {
    const { branches } = await makeGqlRequest(getBranches, payload, context.state.jwt)
    context.commit(mutations.setBranches.name, branches.branches)
    context.commit(mutations.setTotalBranches.name, branches.count)
  },
  async fetchJobs (context: ActionContext<State, State>, payload: FetchJobsPayload) {
    const { jobs } = await makeGqlRequest(getJobs, payload, context.state.jwt)
    context.commit(mutations.setJobs.name, jobs.jobs)
    context.commit(mutations.setTotalJobs.name, jobs.count)
  },
  async fetchJobListings (context: ActionContext<State, State>, payload: FetchJobListingsPayload) {
    const { jobListings } = await makeGqlRequest(getJobListings, payload, context.state.jwt)
    context.commit(mutations.setJobListings.name, jobListings.jobListings)
    context.commit(mutations.setTotalJobListings.name, jobListings.count)
  },
  async fetchArticles (context: ActionContext<State, State>, payload: FetchArticlesPayload) {
    const { articles } = await makeGqlRequest(getArticles, payload, context.state.jwt)
    context.commit(mutations.setArticles.name, articles.articles)
    context.commit(mutations.setTotalArticles.name, articles.count)
  },
  async fetchConfigurations (context: ActionContext<State, State>) {
    const { configurations } = await makeGqlRequest(getConfigurations, {}, context.state.jwt)
    context.commit(mutations.setConfigurations.name, configurations)
  },
  async fetchJobListingPlanConfigurations (context: ActionContext<State, State>) {
    const { jobListingPlanConfigurations } = await makeGqlRequest(getJobListingConfiguration, {}, context.state.jwt)
    context.commit(mutations.setJobListingConfigurations.name, jobListingPlanConfigurations)
  },
  async archiveUser (context: ActionContext<State, State>, payload: ArchivedPayload) {
    try {
      const res = await makeGqlRequest(updateUser, payload, context.state.jwt)
      context.commit(mutations.archiveUser.name, res.updateUser)
      return true
    } catch (error) {
      return false
    }
  },
  async deleteJob (context: ActionContext<State, State>, id) {
    try {
      const res = await makeGqlRequest(updateJob, { ...id, isArchived: true }, context.state.jwt)
      context.commit(mutations.deleteJob.name, res.updateJob)
      return true
    } catch (error) {
      return false
    }
  },
  async updateJob (context: ActionContext<State, State>, payload: UpdateJobPayload) {
    try {
      const updateJobResult = await makeGqlRequest(updateJob, payload, context.state.jwt)
      context.commit(mutations.updateJob.name, updateJobResult.updateJob)
      return true
    } catch (error) {
      return false
    }
  },
  async createBranch (context: ActionContext<State, State>, payload: Branch) {
    try {
      const createBranchResult = await makeGqlRequest(createBranch, payload, context.state.jwt)
      context.commit(mutations.addBranch.name, createBranchResult.createBranch)
      return true
    } catch (error) {
      return false
    }
  },
  async createArea (context: ActionContext<State, State>, payload: Area) {
    try {
      const result = await makeGqlRequest(
        createArea,
        {
          name: payload.name,
          emailNotificationRadius: payload.emailNotificationRadius,
          lat: payload.location?.lat ?? 0,
          lng: payload.location?.lng ?? 0
        },
        context.state.jwt
      )
      context.commit(mutations.addArea.name, result.createArea)
      return true
    } catch (error) {
      return false
    }
  },
  async createAuthority (context: ActionContext<State, State>, payload: AuthorityCreateInput) {
    try {
      const result = await makeGqlRequest(
        createAuthority,
        {
          searchText: payload.searchText
        },
        context.state.jwt
      )
      context.commit(mutations.addAuthority.name, result.createAuthority)
      return true
    } catch (error) {
      return false
    }
  },
  async createArticle (context: ActionContext<State, State>, payload: Article) {
    try {
      const createArticleResult = await makeGqlRequest(createArticle, payload, context.state.jwt)
      context.commit(mutations.addArticle.name, createArticleResult.createArticle)
      return true
    } catch (error) {
      return false
    }
  },
  async createJobListing (context: ActionContext<State, State>, payload: UpdateJobListingPayload) {
    try {
      const createJobListingResult = await makeGqlRequest(createJobListing, {
        input: {
          business: payload.jobListing.business,
          jobListing: {
            ...payload.jobListing,
            airsAt: payload.jobListing.airsAt,
            endTime: new Date(payload.jobListing.endTime || ''),
            expiresAt: payload.jobListing.expiresAt,
            hourlyRate: Number(payload.jobListing.hourlyRate),
            isArchived: false,
            jobsAmount: Number(payload.jobListing.jobsAmount),
            startTime: new Date(payload.jobListing.startTime || '')
          }
        }
      }, context.state.jwt)
      context.commit(mutations.addJobListing.name, createJobListingResult.createJobListing)
      return true
    } catch (error) {
      sessionStorage.setItem('createJobListingError', JSON.stringify(error))
      return false
    }
  },
  async updateBranch (context: ActionContext<State, State>, payload: Branch) {
    try {
      const res = await makeGqlRequest(updateBranch, payload, context.state.jwt)
      context.commit(mutations.updateBranch.name, res.updateBranch)
      return true
    } catch (error) {
      return false
    }
  },
  async archiveBranch (context: ActionContext<State, State>, payload: ArchivedPayload) {
    try {
      const res = await makeGqlRequest(updateBranch, payload, context.state.jwt)
      context.commit(mutations.archiveBranch.name, res.updateBranch)
      return true
    } catch (error) {
      return false
    }
  },
  async updateArea (context: ActionContext<State, State>, payload: Area) {
    try {
      const result = await makeGqlRequest(
        updateArea,
        {
          id: payload.id,
          location: payload.location && {
            address: '',
            ...payload.location
          },
          name: payload.name,
          notes: payload.notes,
          emailNotificationRadius: payload.emailNotificationRadius
        },
        context.state.jwt
      )
      context.commit(mutations.updateArea.name, result.updateArea)
      return true
    } catch (error) {
      return false
    }
  },
  async archiveAuthority (context: ActionContext<State, State>, payload: ArchivedPayload) {
    try {
      const res = await makeGqlRequest(updateAuthority, { id: payload.id, isArchived: payload.isArchived }, context.state.jwt)
      context.commit(mutations.archiveAuthority.name, res.updateAuthority)
      return true
    } catch (error) {
      return false
    }
  },
  async activateAuthority (context: ActionContext<State, State>, payload: UpdateAuthorityPayload) {
    try {
      const res = await makeGqlRequest(updateAuthority, payload, context.state.jwt)
      context.commit(mutations.activateAuthority.name, res.updateAuthority)
      return true
    } catch (error) {
      return false
    }
  },
  async updateAuthority (context: ActionContext<State, State>, payload: UpdateAuthorityPayload) {
    try {
      const result = await makeGqlRequest(
        updateAuthority,
        {
          id: payload.id,
          name: payload.name,
          notes: payload.notes,
          isArchived: payload.isArchived,
          isActive: payload.isActive,
          mayor: payload.mayor,
          mayorPic: payload.mayorPic,
          logo: payload.logo,
          message: payload.message,
          leadArticle: payload.leadArticle
        },
        context.state.jwt
      )
      context.commit(mutations.updateAuthority.name, result.updateAuthority)
      return true
    } catch (error) {
      return false
    }
  },
  async updateJobListing (context: ActionContext<State, State>, payload: UpdateJobListingPayload) {
    try {
      const res = await makeGqlRequest(updateJobListing, {
        id: payload.id,
        data: payload.isBlocking ? {
          isBlocked: payload.jobListing?.isBlocked
        } : {
          ...payload.jobListing,
          airsAt: payload.jobListing.airsAt,
          endTime: new Date(payload.jobListing.endTime || ''),
          expiresAt: payload.jobListing.expiresAt,
          hourlyRate: Number(payload.jobListing.hourlyRate),
          isArchived: false,
          jobsAmount: Number(payload.jobListing.jobsAmount),
          startTime: new Date(payload.jobListing.startTime || '')
        },
        isBlocking: payload.isBlocking
      }, context.state.jwt)
      context.commit(mutations.updateJobListing.name, res.updateJobListing)
      return true
    } catch (error) {
      return false
    }
  },
  async updateArticle (context: ActionContext<State, State>, payload: UpdateArticlePayload) {
    try {
      const res = await makeGqlRequest(updateArticle, payload, context.state.jwt)
      context.commit(mutations.updateArticle.name, res.updateArticle)
      return true
    } catch (error) {
      return false
    }
  },
  async updateConfiguration (context: ActionContext<State, State>, payload: UpdateConfigurationPayload) {
    try {
      const res = await makeGqlRequest(updateConfiguration, payload, context.state.jwt)
      context.commit(mutations.updateConfiguration.name, res.updateConfiguration)
      return true
    } catch (error) {
      return false
    }
  },
  signOut (context: ActionContext<State, State>) {
    context.commit(mutations.setUser.name, null)
    context.commit(mutations.setToken.name, '')
  },
  async upsertJobListingPlanConfiguration (context: ActionContext<State, State>, payload: JobListingPlanConfiguration) {
    try {
      await makeGqlRequest(upsertJobListingPlanConfiguration, { data: payload }, context.state.jwt)
      const { jobListingPlanConfigurations } = await makeGqlRequest(getJobListingConfiguration, {}, context.state.jwt)
      context.commit(mutations.setJobListingConfigurations.name, jobListingPlanConfigurations)
      await context.dispatch('fetchJobListingPlanConfigurations')
      return true
    } catch (error) {
      return false
    }
  }
}

export default new Vuex.Store<State>({
  state,
  actions,
  getters,
  mutations,
  plugins: [
    createPersistedState({ storage: window.sessionStorage })
  ]
})
